import { useCallback, useEffect, useRef, useState } from 'react'
import type { SendOutcome } from '../lib/api'
import { getAwaitingFinalCheckOrders } from '../lib/api'
import type { FinalCheckOrder } from '../types'
import { classify, confirmLines, recheck } from '../lib/final-check'
import { orderCode } from '../lib/order-code'
import { useSilentRefresh } from '../lib/useSilentRefresh'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { InFlightSection } from './InFlightSection'
import S from '../locales/final-check.json'
import C from '../locales/collect.json'
import { tr, useLang } from '../lib/i18n'

interface Asking {
  order: FinalCheckOrder
  sending: boolean
  answer: (approved: boolean) => void
  returnFocus: HTMLElement | null
}

interface FinalCheckScreenProps {
  onCheck: (orderId: string, expectedTotalNow: string) => Promise<SendOutcome>
}

type RowState =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'queued' }
  | { kind: 'rejected'; message: string }
  | { kind: 'failed' }

interface Notice {
  key: 'finalCheckGone' | 'finalCheckQueued'
  code: string
}

function formatItem(item: FinalCheckOrder['items'][number]): string {
  const name = item.variantNameEn ? `${item.nameEn} — ${item.variantNameEn}` : item.nameEn
  return `${name} × ${item.quantity}`
}

// R78 — Nour's final-check queue: every order, kitchen or shelf, stops at
// AWAITING_FINAL_CHECK one step before dispatch. In CollectScreen's style and tokens.
export function FinalCheckScreen({ onCheck }: FinalCheckScreenProps) {
  const lang = useLang()
  const [orders, setOrders] = useState<FinalCheckOrder[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({})
  const [offlineNotice, setOfflineNotice] = useState<Notice | null>(null)

  const [asking, setAsking] = useState<Asking | null>(null)
  const inFlight = useRef(false)

  // ux3 (c): a silent load (the 20 s refresh) only swaps the list: row messages and the
  // notice stay, no "Loading" line, and a failed silent read keeps the list on screen.
  const load = useCallback((silent: boolean = false) => {
    if (inFlight.current) return
    inFlight.current = true
    if (!silent) {
      setLoading(true)
      setLoadError(false)
    }
    getAwaitingFinalCheckOrders()
      .then((result) => {
        setOrders(result)
        if (silent) return
        setRowStates({})
        setOfflineNotice(null)
      })
      .catch(() => {
        if (!silent) setLoadError(true)
      })
      .finally(() => {
        inFlight.current = false
        if (!silent) setLoading(false)
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // No silent swap while a row is being checked or the ask is open.
  useSilentRefresh(() => {
    if (asking !== null || Object.values(rowStates).some((s) => s.kind === 'pending')) return
    load(true)
  })

  // ux3 (f): resolves when the cashier answers the in-app dialog (replaces window.confirm).
  function askConfirm(order: FinalCheckOrder, returnFocus: HTMLElement | null): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      setAsking({
        order,
        sending: false,
        returnFocus,
        answer: (approved) => {
          if (approved) setAsking((prev) => (prev ? { ...prev, sending: true } : prev))
          resolve(approved)
        },
      })
    })
  }

  async function handleCheck(loaded: FinalCheckOrder) {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const orderId = loaded.id
    setRowStates((prev) => ({ ...prev, [orderId]: { kind: 'pending' } }))
    let order = loaded
    try {
      // One re-read, so a total that moved since the list loaded is asked about with
      // its fresh amounts. Any failure of the re-read goes on with the loaded row.
      try {
        const fresh = await getAwaitingFinalCheckOrders()
        const result = recheck(loaded, fresh, orderId)
        if (result.kind === 'gone') {
          setOfflineNotice({ key: 'finalCheckGone', code: orderCode(loaded) })
          setOrders((prev) => prev && prev.filter((o) => o.id !== orderId))
          setRowStates((prev) => ({ ...prev, [orderId]: { kind: 'idle' } }))
          return
        }
        if (result.kind === 'changed') {
          order = result.row
          setOrders((prev) => prev && prev.map((o) => (o.id === orderId ? result.row : o)))
        } else {
          order = fresh.find((r) => r.id === orderId) ?? loaded
        }
      } catch {
        order = loaded
      }
      if (classify(order) !== 'one-click' && !(await askConfirm(order, opener))) {
        setRowStates((prev) => ({ ...prev, [orderId]: { kind: 'idle' } }))
        return
      }
      const outcome = await onCheck(orderId, order.totalNow)
      if (outcome.outcome === 'rejected') {
        setRowStates((prev) => ({ ...prev, [orderId]: { kind: 'rejected', message: outcome.message } }))
        return
      }
      if (outcome.outcome === 'queued') {
        // Screen-level notice, not drawn inside the row: the row leaves in this same
        // step, so a notice drawn inside it would never be seen (Chuck #41619).
        setOfflineNotice({ key: 'finalCheckQueued', code: orderCode(order) })
        setRowStates((prev) => ({ ...prev, [orderId]: { kind: 'queued' } }))
        setOrders((prev) => prev && prev.filter((o) => o.id !== orderId))
        return
      }
      setOrders((prev) => prev && prev.filter((o) => o.id !== orderId))
    } catch {
      setRowStates((prev) => ({
        ...prev,
        [orderId]: { kind: 'failed' },
      }))
    } finally {
      // Closes the ask in the same render as the row's new state, so focus can go back
      // to an enabled row button.
      setAsking(null)
    }
  }

  return (
    <div className="min-h-screen bg-fleet-50 px-6 py-8 font-body text-fleet-800">
      <div className="w-full max-w-sm mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-condensed text-2xl font-bold uppercase tracking-wide">{tr(S.finalCheckTitle, lang)}</h1>
          <button
            id="final-check-refresh-btn"
            type="button"
            onClick={() => load()}
            className="min-h-tap text-sm font-semibold text-fleet-500 px-2"
          >
            {tr(C.refresh, lang)}
          </button>
        </div>

        <InFlightSection onPacked={() => load(true)} finalCheckOrders={orders} />

        {offlineNotice && (
          <p
            id="final-check-offline-notice"
            role="status"
            className="mb-4 rounded-lg bg-fleet-100 text-fleet-700 text-sm font-semibold px-4 py-3"
          >
            {tr(S[offlineNotice.key], lang, { code: offlineNotice.code })}
          </p>
        )}

        {loading && <p className="text-sm text-fleet-500">{tr(C.loading, lang)}</p>}

        {loadError && (
          <div className="space-y-3">
            <div
              id="final-check-load-error"
              role="alert"
              className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3"
            >
              {tr(S.finalCheckLoadError, lang)}
            </div>
            <button
              id="final-check-try-again-btn"
              type="button"
              onClick={() => load()}
              className="w-full min-h-tap rounded-lg bg-fleet-700 text-white font-semibold px-4 py-2"
            >
              {tr(S.tryAgain, lang)}
            </button>
          </div>
        )}

        {!loading && !loadError && orders !== null && orders.length === 0 && (
          <p id="final-check-empty" className="text-sm text-fleet-500">
            {tr(S.finalCheckEmpty, lang)}
          </p>
        )}

        {!loading && !loadError && orders !== null && orders.length > 0 && (
          <ul id="final-check-list" className="space-y-3">
            {orders.map((order) => {
              const state = rowStates[order.id] ?? { kind: 'idle' }
              return (
                <li
                  key={order.id}
                  data-order-id={order.id}
                  className="rounded-lg border border-fleet-300 px-4 py-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{orderCode(order)}</span>
                    <span className="text-sm text-fleet-500">
                      {order.fulfillmentMethod === 'delivery' ? tr(S.delivery, lang) : tr(S.pickup, lang)}
                    </span>
                  </div>
                  <p className="text-sm text-fleet-600">{order.customerName ?? tr(C.unknownCustomer, lang)}</p>
                  <ul className="text-sm text-fleet-700 space-y-0.5">
                    {order.items.map((item, idx) => (
                      <li key={idx}>{formatItem(item)}</li>
                    ))}
                  </ul>

                  {(state.kind === 'rejected' || state.kind === 'failed') && (
                    <div
                      role="alert"
                      className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3"
                    >
                      {state.kind === 'failed' ? tr(C.submitFailed, lang) : state.message}
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={state.kind === 'pending'}
                    onClick={() => handleCheck(order)}
                    className="w-full min-h-tap rounded-lg bg-amber-500 disabled:bg-fleet-300 text-white font-semibold px-4 py-2"
                  >
                    {state.kind === 'pending' ? tr(S.checking, lang) : tr(S.checkedReady, lang)}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {asking !== null && (() => {
        const lines = confirmLines(asking.order)
        return (
          <ConfirmDialog
            title={tr(classify(asking.order) === 'changed' ? S.confirmTitleChanged : S.confirmTitleCheck, lang)}
            line={lines?.[lang] ?? ''}
            lang={lang}
            sending={asking.sending}
            onApprove={() => asking.answer(true)}
            onCancel={() => asking.answer(false)}
            returnFocus={asking.returnFocus}
            fallbackFocusId="final-check-refresh-btn"
          />
        )
      })()}
    </div>
  )
}
